import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import dayjs from "dayjs";
import { FormikHandlers, FormikHelpers, FormikState } from "formik";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomSelect from "@/components/CustomSelect";
import ErrorHelper from "@/components/ErrorWrapper";
import Flex from "@/components/Flex";
import NumberInput from "@/components/NumberInput";
import Text from "@/components/Text";
import WeekdayChooser from "./WeekChooser";

/** Recurring fields any host form must carry to render this config. */
export type RecurringFormValues = {
  isRecurring: boolean;
  recurringFrequency: RecurringFrequency;
  endType: EndType;
  endDate: dayjs.ConfigType;
  endAfter: number | string;
  customFrequency: number | string;
  customFrequencyUnit: CustomFrequencyUnit;
  selectedWeekdays: number[];
  /** Lower bound for the End On picker. */
  startTime?: dayjs.ConfigType;
};

export const RecurringConfig = <T extends RecurringFormValues>({
  formik,
  required = false,
  disabled = false,
}: {
  formik: Formik<T>;
  required?: boolean;
  disabled?: boolean;
}) => {
  const { setFieldValue } = formik;
  return (
    <Grid container spacing={2} rowSpacing={1} size={12}>
      <Grid size={12}>
        <CustomSelect
          required={required}
          value={formik.values.recurringFrequency}
          label="Frequency"
          name="recurringFrequency"
          placeholder="Frequency of recurring"
          options={recurringFrequencySelectItems}
          disabled={disabled}
          onChange={(value) => setFieldValue("recurringFrequency", value)}
          error={
            formik.touched.recurringFrequency &&
            (formik.errors.recurringFrequency as any)
          }
        />
      </Grid>

      <RecurringEventEndConfig formik={formik} disabled={disabled} />

      {formik.values.recurringFrequency === "custom" && (
        <CustomFrequencyConfig
          formik={formik}
          required={required}
          disabled={disabled}
        />
      )}
    </Grid>
  );
};

const RecurringEventEndConfig = <T extends RecurringFormValues>({
  formik,
  disabled = false,
}: {
  formik: Formik<T>;
  disabled?: boolean;
}) => {
  return (
    <Grid container size={12}>
      <Grid
        size={{
          sm: 7,
        }}
      >
        <Flex gap={1} alignItems={"center"} ml={0.5}>
          <Text>
            Ends<sup style={{ color: "red" }}>*</sup>:
          </Text>

          <ErrorHelper
            error={
              formik.touched.isRecurring && (formik.errors.endType as string)
            }
          >
            <RadioGroup
              row
              name="endType"
              onBlur={formik.handleBlur}
              defaultValue={formik.values.endType}
              onChange={(e) => {
                formik.setFieldValue("endType", e.currentTarget.value);
                if (e.currentTarget.value === "after_n_events") {
                  formik.setFieldValue("endDate", null);
                }
              }}
            >
              {recurringEventEndTypeRadioItems.map((props) => (
                <FormControlLabel
                  defaultChecked={props.value === formik.values.endType}
                  value={props.value}
                  control={<Radio />}
                  label={props.label}
                  disabled={disabled}
                />
              ))}
            </RadioGroup>
          </ErrorHelper>
        </Flex>
      </Grid>
      <Grid
        sx={{
          mt: 1,
        }}
        size={{
          sm: 5,
        }}
      >
        {formik.values.endType === "on_date" && (
          // <DateInput
          //   label="End on"
          //   error={formik.errors.endDate && `${formik.errors.endDate}`}
          //   name="endDate"
          //   onChange={handleChange("endDate")}
          //   value={formik.values.endDate}
          // />
          <CustomDatePicker
            label="End On"
            // format="YYYY-MM-DD"
            required
            name="endDate"
            disabled={disabled}
            handleBlur={formik.handleBlur}
            minDate={dayjs(formik.values?.startTime)}
            value={
              formik.values.endDate ? dayjs(formik.values.endDate) : undefined
            }
            onChange={(val) => {
              formik.setFieldValue("endDate", val);
            }}
            error={formik.touched.endDate && (formik.errors.endDate as any)}
          />
        )}

        {formik.values.endType === "after_n_events" && (
          <NumberInput
            required
            min={0}
            textFieldProps={{
              name: "endAfter",
              disabled,
              onBlur: formik.handleBlur,
              value: formik.values.endAfter,
              onChange: formik.handleChange,
            }}
            label="End after these number"
            error={formik.touched.endAfter && (formik.errors.endAfter as any)}
          />
        )}
      </Grid>
    </Grid>
  );
};

type Formik<Value> = FormikState<Value> & FormikHelpers<Value> & FormikHandlers;

const CustomFrequencyConfig = <T extends RecurringFormValues>({
  formik,
  required = false,
  disabled = false,
}: {
  formik: Formik<T>;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          mt: "lg",
          marginTop: "5px",
          pl: 1,
        }}
      >
        <Grid
          sx={{
            justifyContent: "center",
          }}
          size={{
            sm: 6,
          }}
        >
          <NumberInput
            required={required}
            textFieldProps={{
              name: "customFrequency",
              disabled,
              onBlur: formik.handleBlur,
              value: formik.values.customFrequency,
              onChange: formik.handleChange,
            }}
            label="Repeat every"
            error={
              formik.touched.customFrequency &&
              (formik.errors.customFrequency as any)
            }
            // min={1}
            // precision={0}
            // step={1}
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
          }}
        >
          <CustomSelect
            required={required}
            value={formik.values.customFrequencyUnit}
            error={
              formik.touched.customFrequencyUnit &&
              (formik.errors.customFrequencyUnit as any)
            }
            label=""
            name="customFrequencyUnit"
            disabled={disabled}
            handleBlur={formik.handleBlur}
            options={customFrequencyTypeSelectItems}
            onChange={(value) =>
              formik.setFieldValue("customFrequencyUnit", value)
            }
          />
        </Grid>
      </Grid>

      {formik.values.customFrequencyUnit === "weeks" && (
        <Grid size={12}>
          <WeekdayChooser
            label="Select the days to recur on"
            selectedDays={formik.values.selectedWeekdays}
            disabled={disabled}
            onChange={(value) =>
              formik.setFieldValue("selectedWeekdays", value)
            }
            error={formik.errors.selectedWeekdays as string}
          />
        </Grid>
      )}
    </>
  );
};

type SelectItem<T> = {
  value: T;
  label: string;
};

export type RecurringFrequency = "daily" | "weekly" | "monthly" | "custom";
export type CustomFrequencyUnit = "days" | "weeks" | "months";
export type EndType = "never" | "on_date" | "after_n_events";

const customFrequencyTypeSelectItems: SelectItem<CustomFrequencyUnit>[] = [
  {
    value: "days",
    label: "Days",
  },
  {
    value: "weeks",
    label: "Weeks",
  },
  {
    value: "months",
    label: "Months",
  },
];

const recurringFrequencySelectItems: SelectItem<RecurringFrequency>[] = [
  {
    value: "daily",
    label: "Daily",
  },
  {
    value: "weekly",
    label: "Weekly",
  },
  {
    value: "monthly",
    label: "Monthly",
  },
  {
    value: "custom",
    label: "Custom",
  },
];

const recurringEventEndTypeRadioItems: SelectItem<EndType>[] = [
  {
    value: "on_date",
    label: "On",
  },
  {
    value: "after_n_events",
    label: "After",
  },
];
